/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import {
    type AncestorNode,
    Block,
    type Document,
    type Fragment,
    type Inline,
    type Leaf,
    Text,
} from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import type { EditorContainer, NodeDOMAttributes } from '../interfaces';
import { OffsetKey } from '../utils';
import { renderLeafText } from './LeafRenderer';

/**
 * Render a virtual placholder for a node.
 * The placeholder is invisible but contains the whole markup to make the browser work with it.
 */
export const NodeVirtualRenderer = React.memo(function NodeVirtualRendererMemo(props: {
    node: Block | Inline;
    editor: EditorContainer;
    parent: Block | Document | Fragment | Inline;
    ancestors: List<AncestorNode>;
    height?: number | undefined;
    attributes?: NodeDOMAttributes;
    domRef?: React.RefCallback<Element>;
}): React.ReactElement {
    const { editor, node, parent, ancestors, height, domRef, attributes: attrs } = props;

    const attributes: NodeDOMAttributes = {
        'data-key': node.key,
        ...(attrs || {}),
    };
    const childrenAncestors = ancestors.push(node);

    // Render the children
    const nodes = node.nodes.map((child: Block | Inline | Text) => {
        if (Text.isText(child)) {
            return <TextVirtualRenderer key={child.key} node={child} parent={node} />;
        }

        return (
            <NodeVirtualRenderer
                key={child.key}
                node={child}
                editor={editor}
                parent={node}
                ancestors={childrenAncestors}
            />
        );
    });

    // Render the fragments.
    const fragments = (Block.isBlock(node) && node.fragments ? node.fragments : []).reduce<
        React.ReactElement[]
    >((fragments, fragment, key) => {
        return fragments.concat(
            <FragmentVirtualRenderer
                key={key}
                fragment={fragment}
                editor={editor}
                ancestors={childrenAncestors}
            />
        );
    }, []);

    const children = [...nodes, ...fragments];

    const style: React.CSSProperties = {
        height,
        opacity: 0,
    };

    const element = Block.isBlock(node)
        ? editor.stack.find('renderVirtualNode', {
              attributes,
              node,
              parent: parent as Block | Document | Fragment,
              ancestors,
              virtualHeight: height,
              style,
              children,
              domRef,
          })
        : null;

    return (
        element || (
            <div {...attributes} data-virtual ref={domRef} style={style}>
                {children}
            </div>
        )
    );
});

/**
 * Render a virtual text node. Designed to render simply the leaves of the node,
 * without any decorations or selections. See the Slate TextRenderer.
 */
const TextVirtualRenderer = React.memo(function TextVirtualRendererMemo(props: {
    node: Text;
    parent: Block | Inline;
}): React.ReactElement {
    const { node, parent } = props;

    return (
        <span key={node.key} data-key={node.key}>
            {node.leaves.map((leaf, leafIndex) => (
                <LeafVirtualRenderer
                    key={`${node.key}-leaf:${leafIndex}`}
                    leaf={leaf}
                    node={node}
                    parent={parent}
                    index={leafIndex}
                />
            ))}
        </span>
    );
});

/**
 * Render a virtual fragment.
 */
const FragmentVirtualRenderer = React.memo(function FragmentVirtualRendererMemo(props: {
    fragment: Fragment;
    editor: EditorContainer;
    ancestors: List<AncestorNode>;
}): React.ReactElement {
    const { fragment, editor, ancestors } = props;

    return (
        <div data-key={fragment.key}>
            {fragment.nodes.map((child: Block) => {
                return (
                    <NodeVirtualRenderer
                        key={child.key}
                        node={child}
                        editor={editor}
                        parent={fragment}
                        ancestors={ancestors}
                    />
                );
            })}
        </div>
    );
});

/**
 * Render a virtual leaf node. Designed to render simply the text, without
 * any decoration or selection. See the Slate LeafRenderer.
 */
const LeafVirtualRenderer = React.memo(function LeafVirtualRendererMemo(props: {
    leaf: Leaf;
    index: number;
    node: Text;
    parent: Block | Inline;
}): React.ReactElement {
    const { node, parent, index, leaf } = props;

    const offsetKey = OffsetKey.stringify({
        key: node.key,
        index,
    });

    return (
        <span key={offsetKey} data-offset-key={offsetKey} data-slate-leaf>
            {renderLeafText({
                node,
                block: parent,
                text: leaf.text,
                index,
                parent,
                leaves: node.leaves,
            })}
        </span>
    );
});
